import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import "react-colorful/dist/index.css";

// components
import SaveToKits from "core/SaveToKits";
import BodyText from "core/BodyText";
import CopyButton from "core/CopyButton";
import ImagePreventRightClick from "core/ImagePreventRightClick";
import ColorPicker from "icons/ColorPicker";
import EditorRotate from "./EditorRotate";
import EditorFlip from "./EditorFlip";
import ShapeControls from "./ShapeControls";
import FortLinks from "./FortLinks";
import ProcessingScreen from "./ProcessingScreen";
import EditorReset from "./EditorReset";

// styles
import colors from "core/colors";
import Flexbox from "core/Flexbox";
import {
    IconEditorContainer,
    IconContainer,
    EditingDetailsContainer,
    EditingDetailsTopRow,
    EditingDetailsBottomRow,
    EditingColumn,
    EditingBodyText,
    EditingControlContent,
    RotateIconContainer
} from "./styles";

import {
    useIconEditorState,
    useIconEditorDispatch,
    useIconIsEdited,
    useIconEditorVisibility
} from "js/contexts/IconEditorContext";
import { useViewport } from "js/hooks/useViewport";

import { DEFAULT_CONTAINER_COLOR } from "./constants";

const IconEditor = ({ icon, isSuperuser }) => {
    const dispatch = useIconEditorDispatch();
    const state = useIconEditorState();
    const { iconColors, fetchingPreviewImage, backgroundColors, shape } = state;

    const { browserWidth } = useViewport();

    const [showClippy, setShowClippy] = useState(false);

    const handleEditorResetButtonClick = useCallback(() => {
        dispatch({
            type: "RESET_EDITOR"
        });
    }, []);

    const { showControls, showAttribution } = useIconEditorVisibility();
    const iconIsEdited = useIconIsEdited({ iconId: icon ? icon.id : null });

    // reset the editor if the controls are being hidden
    useEffect(() => {
        if (!showControls && iconIsEdited) {
            handleEditorResetButtonClick();
        }
    }, [showControls, iconIsEdited]);

    if (!icon) return null;

    const creatorId = icon.creator && icon.creator.id;
    const iconContainerColor = iconIsEdited ? "" : DEFAULT_CONTAINER_COLOR;
    const { editorPreview, flipX = false, flipY = false } =
        state[icon.id] || {};
    const iconPreview =
        showAttribution && icon.assets && icon.assets.attribution600
            ? icon.assets.attribution600
            : editorPreview;

    return (
        <>
            <FortLinks
                isSuperuser={isSuperuser}
                iconId={icon.id}
                creatorId={creatorId}
            />

            <IconEditorContainer>
                <IconContainer
                    onMouseOver={() => setShowClippy(true)}
                    onMouseLeave={() => setShowClippy(false)}
                    browserWidth={browserWidth}
                    color={iconContainerColor}
                >
                    {iconPreview && (
                        <ImagePreventRightClick
                            data-testid="icon-editor-preview"
                            imageSrc={iconPreview}
                            srText={`${icon.title} Icon ${icon.id}`}
                        />
                    )}
                    {!fetchingPreviewImage && showClippy && (
                        <CopyButton icon={icon} editorValues={state} />
                    )}
                    {fetchingPreviewImage && <ProcessingScreen />}
                </IconContainer>
                <EditingDetailsContainer show={showControls}>
                    <EditingDetailsTopRow>
                        <BodyText
                            type="p"
                            size="md"
                            copyColor={colors.secondary.orca}
                        >
                            Customize this icon
                        </BodyText>
                    </EditingDetailsTopRow>
                    <EditingDetailsBottomRow>
                        {/* "Icon" related controls: */}
                        <EditingColumn>
                            <EditingBodyText>Icon</EditingBodyText>

                            <Flexbox alignItems="center" width="100%">
                                <EditingControlContent>
                                    <ColorPicker
                                        title="Icon Color"
                                        activeColor={iconColors.activeColor}
                                        recent={iconColors.recent}
                                        palette={iconColors.palette}
                                        colorTextInputValue={
                                            iconColors.colorTextInputValue
                                        }
                                        shape={shape}
                                    />
                                </EditingControlContent>

                                <EditingControlContent>
                                    <RotateIconContainer>
                                        <EditorRotate
                                            iconId={icon.id}
                                            direction="ccw"
                                        />
                                    </RotateIconContainer>
                                    <RotateIconContainer>
                                        <EditorRotate
                                            iconId={icon.id}
                                            direction="cw"
                                        />
                                    </RotateIconContainer>
                                </EditingControlContent>

                                <EditingControlContent>
                                    <RotateIconContainer>
                                        <EditorFlip
                                            iconId={icon.id}
                                            axis="y"
                                            flipX={flipX}
                                            flipY={flipY}
                                        />
                                    </RotateIconContainer>
                                    <RotateIconContainer>
                                        <EditorFlip
                                            iconId={icon.id}
                                            axis="x"
                                            flipX={flipX}
                                            flipY={flipY}
                                        />
                                    </RotateIconContainer>
                                </EditingControlContent>
                            </Flexbox>
                        </EditingColumn>

                        {/* "Background" related controls: */}
                        <EditingColumn>
                            <EditingBodyText>Background</EditingBodyText>

                            <Flexbox alignItems="center" width="100%">
                                <ShapeControls
                                    activeColor={backgroundColors.activeColor}
                                    recent={backgroundColors.recent}
                                    palette={backgroundColors.palette}
                                    colorTextInputValue={
                                        backgroundColors.colorTextInputValue
                                    }
                                    shape={shape}
                                />
                            </Flexbox>
                        </EditingColumn>

                        <EditingColumn>
                            <EditorReset
                                handleClick={handleEditorResetButtonClick}
                                isDisabled={!iconIsEdited}
                            />
                        </EditingColumn>
                    </EditingDetailsBottomRow>
                </EditingDetailsContainer>

                <SaveToKits iconId={icon.id} />
            </IconEditorContainer>
        </>
    );
};

IconEditor.propTypes = {
    icon: PropTypes.object,
    isSuperuser: PropTypes.bool.isRequired
};

export default IconEditor;
